import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import TrDateInput from './TrDateInput';
import FiyatKontrolUyari from './FiyatKontrolUyari';
import FiyatAraliklariModal from './FiyatAraliklariModal';
import AiOdemePlaniSihirbazi from './AiOdemePlaniSihirbazi';
import { FiyatlandirmaKurali, kontrolEt, KontrolSonucu, ortalamaVadeGun, sonOdemeTarihiMesaji, tarihIzinliMi } from '../utils/fiyatlandirmaKontrol';

// v1.26.0 — Yeni satış sözleşmesi ekranında ödeme planı: sunum teklifindeki ve sözleşme detayındaki gibi şablon,
// toplu taksit, elle kalem; kilitli kalemler korunarak eşit dağıtım; ortalama vade (sözleşme tarihine göre) ve proje
// fiyatlandırma kuralı kontrolü. Plan sözleşme kaydedilince OdemePlanTaksitleri'ne yazılır.

export interface PlanKalemi {
    vadeTarihi: string;     // YYYY-MM-DD
    miktar: number;
    odemeTuru: string;      // Nakit | BankaHavale | Senet | Cek | Takas
    aciklama: string;
    kilitli: boolean;
}

interface SablonDetay { detayId: string; odemeTuru: string; vadeAyEndeksi: number; hesaplamaYontemi: string; deger: number; }
interface Sablon {
    sablonId: string; sablonAdi: string; pesinatTipi: string; pesinatDegeri: number; vadeTipi: string;
    maksimumTaksitSayisi: number; isActive: boolean; detaylar: SablonDetay[];
}

export const ODEME_TURLERI: [string, string][] = [['Nakit', 'Nakit'], ['BankaHavale', 'Banka Havalesi'], ['Senet', 'Senet'], ['Cek', 'Çek'], ['Takas', 'Takas']];

const yuvarla = (n: number) => Math.round(n);
const tl = (n: number) => `${Math.round(n).toLocaleString('tr-TR')} ₺`;
const ayEkle = (iso: string, ay: number) => {
    const [y, m, d] = iso.split('-').map(Number);
    const t = new Date(y, m - 1 + ay, 1);
    const sonGun = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    t.setDate(Math.min(d, sonGun));
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};
const sirala = (k: PlanKalemi[]) => [...k].sort((a, b) => a.vadeTarihi.localeCompare(b.vadeTarihi));

/** Kilitsiz kalemlere (toplam − kilitliler) eşit dağıtır; yuvarlama farkı son kilitsiz kaleme eklenir. */
export const esitDagit = (kalemler: PlanKalemi[], toplam: number): PlanKalemi[] => {
    const kilitsiz = kalemler.map((k, i) => (k.kilitli ? -1 : i)).filter(i => i >= 0);
    if (kilitsiz.length === 0) return kalemler;
    const kalan = toplam - kalemler.filter(k => k.kilitli).reduce((s, k) => s + k.miktar, 0);
    if (kalan <= 0) return kalemler;
    const pay = Math.floor(kalan / kilitsiz.length);
    const son = kilitsiz[kilitsiz.length - 1];
    return kalemler.map((k, i) => (k.kilitli ? k : { ...k, miktar: i === son ? kalan - pay * (kilitsiz.length - 1) : pay }));
};

/**
 * Teklif ekranındaki davranışın aynısı: toplamdan kilitli kalemler düşülür, kalan kilitsiz kalemlere önceki tutarlarıyla
 * orantılı dağıtılır (hepsi 0'sa eşit); yuvarlama farkı son kilitsiz kaleme eklenir.
 */
export const oranliDagit = (kalemler: PlanKalemi[], toplam: number): PlanKalemi[] => {
    const kilitsiz = kalemler.map((k, i) => (k.kilitli ? -1 : i)).filter(i => i >= 0);
    if (kilitsiz.length === 0 || toplam <= 0) return kalemler;
    const kalan = Math.max(0, toplam - kalemler.filter(k => k.kilitli).reduce((s, k) => s + (k.miktar || 0), 0));
    const onceki = kilitsiz.reduce((s, i) => s + (kalemler[i].miktar || 0), 0);
    const yeni = [...kalemler];
    let dagitilan = 0;
    kilitsiz.forEach((idx, pos) => {
        let pay: number;
        if (pos === kilitsiz.length - 1) pay = kalan - dagitilan;
        else {
            pay = Math.round(kalan * (onceki > 0 ? (kalemler[idx].miktar || 0) / onceki : 1 / kilitsiz.length));
            dagitilan += pay;
        }
        yeni[idx] = { ...yeni[idx], miktar: Math.max(0, pay) };
    });
    return yeni;
};

/** Planın sözleşme tutarıyla uyumu ve fiyat kuralı kontrolü — kayıt öncesi doğrulama için dışarı da açılır. */
export const planDurumu = (p: { kalemler: PlanKalemi[]; toplam: number; sozlesmeTarihi: string; kural: FiyatlandirmaKurali | null; listeFiyati?: number | null; kullaniciID?: number | null }) => {
    const planToplam = p.kalemler.reduce((s, k) => s + (k.miktar || 0), 0);
    const ortalamaVade = ortalamaVadeGun(p.kalemler.map(k => ({ vadeTarihi: k.vadeTarihi, miktar: k.miktar })), p.sozlesmeTarihi);
    const kontrol: KontrolSonucu = kontrolEt({
        kural: p.kural, listeFiyati: p.listeFiyati, satisFiyati: p.toplam, ortalamaVade,
        vadeTarihleri: p.kalemler.map(k => k.vadeTarihi), kullaniciID: p.kullaniciID,
    });
    return { planToplam, fark: p.toplam - planToplam, ortalamaVade, kontrol, tamam: p.kalemler.length > 0 && Math.abs(p.toplam - planToplam) < 1 };
};

const OdemePlaniOlusturucu: React.FC<{
    toplam: number;               // sözleşme fiyatı
    sozlesmeTarihi: string;       // YYYY-MM-DD — vade ve ortalama vade buna göre
    listeFiyati?: number | null;
    kural: FiyatlandirmaKurali | null;
    kullaniciID?: number | null;
    kalemler: PlanKalemi[];
    onChange: (k: PlanKalemi[]) => void;
}> = ({ toplam, sozlesmeTarihi, listeFiyati, kural, kullaniciID, kalemler, onChange }) => {
    const [sablonlar, setSablonlar] = useState<Sablon[]>([]);
    const [sablonId, setSablonId] = useState('');
    const [sablonTuru, setSablonTuru] = useState('Nakit');
    const [toplu, setToplu] = useState({ tutar: '', sayi: '12', ilk: '', aralik: '1', tur: 'Senet', aciklama: 'Taksit' });
    const [yeni, setYeni] = useState({ vade: '', tutar: '', tur: 'Nakit', aciklama: '' });
    const [araliklarAcik, setAraliklarAcik] = useState(false);
    const [aiAcik, setAiAcik] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/OdemePlanSablonlari').then(r => setSablonlar((r.data || []).filter((s: Sablon) => s.isActive))).catch(() => setSablonlar([]));
    }, []);

    const durum = planDurumu({ kalemler, toplam, sozlesmeTarihi, kural, listeFiyati, kullaniciID });
    const tarihUygun = (...t: string[]) => {
        if (t.every(x => tarihIzinliMi(kural, x, kullaniciID))) return true;
        toast.error(sonOdemeTarihiMesaji(kural!));
        return false;
    };

    // Şablon: peşinat (sözleşme tarihinde) + şablon detayları (ay endeksli) + kalan eşit taksitler
    const sablonUygula = () => {
        const s = sablonlar.find(x => x.sablonId === sablonId);
        if (!s) return;
        if (toplam <= 0) { toast.error('Önce sözleşme fiyatını girin.'); return; }
        if (kalemler.length > 0 && !window.confirm('Mevcut plan silinip şablona göre yeniden oluşturulsun mu?')) return;
        const satir: (PlanKalemi & { ay: number })[] = [];
        const pesinat = s.pesinatTipi === 'Oran' ? yuvarla(toplam * s.pesinatDegeri / 100) : Math.min(yuvarla(s.pesinatDegeri), yuvarla(toplam));
        if (pesinat > 0) satir.push({ vadeTarihi: sozlesmeTarihi, miktar: pesinat, odemeTuru: sablonTuru, aciklama: 'Peşinat', kilitli: false, ay: 0 });
        let kalan = toplam - pesinat;
        const detaylar = [...s.detaylar].sort((a, b) => a.vadeAyEndeksi - b.vadeAyEndeksi);
        const dolu = new Set<number>();
        detaylar.forEach(d => {
            const tutar = d.hesaplamaYontemi === 'ToplaminYuzdesi' ? yuvarla(toplam * d.deger / 100)
                : d.hesaplamaYontemi === 'KalaninYuzdesi' ? yuvarla(kalan * d.deger / 100) : yuvarla(d.deger);
            kalan -= tutar;
            dolu.add(d.vadeAyEndeksi);
            satir.push({ vadeTarihi: ayEkle(sozlesmeTarihi, d.vadeAyEndeksi), miktar: tutar, odemeTuru: sablonTuru, aciklama: d.odemeTuru, kilitli: false, ay: d.vadeAyEndeksi });
        });
        const n = s.vadeTipi === 'SerbestOdeme' ? 0 : Math.max(0, s.maksimumTaksitSayisi - detaylar.length);
        if (n > 0 && kalan > 0) {
            const aylar: number[] = [];
            for (let ay = 1; aylar.length < n; ay++) if (!dolu.has(ay)) aylar.push(ay);
            const pay = Math.floor(kalan / n);
            aylar.forEach((ay, i) => satir.push({
                vadeTarihi: ayEkle(sozlesmeTarihi, ay), miktar: i < n - 1 ? pay : kalan - pay * (n - 1),
                odemeTuru: sablonTuru, aciklama: `${i + 1}. Taksit`, kilitli: false, ay,
            }));
        }
        const yeniKalemler = satir.sort((a, b) => a.ay - b.ay).map(({ ay, ...k }) => k);
        if (!tarihUygun(...yeniKalemler.map(k => k.vadeTarihi))) return;
        onChange(yeniKalemler);
    };

    const topluEkle = () => {
        const sayi = parseInt(toplu.sayi) || 0;
        const tutar = parseFloat(toplu.tutar.replace(/\./g, '').replace(',', '.')) || Math.max(0, durum.fark);
        const aralik = Math.max(1, parseInt(toplu.aralik) || 1);
        const ilk = toplu.ilk || ayEkle(sozlesmeTarihi, 1);
        if (sayi <= 0 || tutar <= 0) { toast.error('Taksit sayısı ve tutar 0\'dan büyük olmalı.'); return; }
        const pay = Math.floor(tutar / sayi);
        const ekler: PlanKalemi[] = Array.from({ length: sayi }, (_, i) => ({
            vadeTarihi: ayEkle(ilk, i * aralik), miktar: i < sayi - 1 ? pay : tutar - pay * (sayi - 1),
            odemeTuru: toplu.tur, aciklama: `${toplu.aciklama || 'Taksit'} ${i + 1}/${sayi}`, kilitli: false,
        }));
        if (!tarihUygun(...ekler.map(k => k.vadeTarihi))) return;
        onChange(sirala([...kalemler, ...ekler]));
        setToplu(t => ({ ...t, tutar: '' }));
    };

    const tekEkle = () => {
        const tutar = parseFloat(yeni.tutar.replace(/\./g, '').replace(',', '.')) || 0;
        if (!yeni.vade || tutar <= 0) { toast.error('Vade tarihi ve tutar girin.'); return; }
        if (!tarihUygun(yeni.vade)) return;
        // Elle girilen tutar kilitlenir; kalan, kilitsiz kalemlere dağıtılır
        const k = sirala([...kalemler, { vadeTarihi: yeni.vade, miktar: tutar, odemeTuru: yeni.tur, aciklama: yeni.aciklama || 'Ödeme', kilitli: true }]);
        onChange(k.some(x => !x.kilitli) ? oranliDagit(k, toplam) : k);
        setYeni(y => ({ ...y, vade: '', tutar: '', aciklama: '' }));
    };

    const guncelle = (i: number, d: Partial<PlanKalemi>) => {
        if (d.vadeTarihi && !tarihUygun(d.vadeTarihi)) return;
        const yeni = kalemler.map((k, j) => (j === i ? { ...k, ...d, ...(d.miktar !== undefined ? { kilitli: true } : {}) } : k));
        // Tutar değişince (teklif ekranındaki gibi) satır kilitlenir, kalan kilitsiz kalemlere orantılı dağıtılır
        onChange(d.miktar !== undefined ? oranliDagit(yeni, toplam) : yeni);
    };

    const giris: React.CSSProperties = { padding: '6px 8px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
    const etiket: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' };
    const kutu: React.CSSProperties = { border: '1.5px solid #f1f5f9', borderRadius: 12, padding: 14, background: '#fafbfc' };
    const btn: React.CSSProperties = { padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

    return (
        <div>
            {/* Özet */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
                {[
                    ['Sözleşme fiyatı', tl(toplam), '#0f172a'],
                    ['Planlanan', tl(durum.planToplam), '#cd6906'],
                    ['Planlanmamış', tl(durum.fark), Math.abs(durum.fark) < 1 ? '#16a34a' : '#dc2626'],
                    ['Kalem', String(kalemler.length), '#0f172a'],
                    ['Ortalama vade', kalemler.length ? `${durum.ortalamaVade} gün` : '–', '#0f172a'],
                ].map(([e, d, r]) => (
                    <div key={e} style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 12, padding: '10px 14px' }}>
                        <div style={etiket}>{e}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: r }}>{d}</div>
                    </div>
                ))}
            </div>

            {kalemler.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                    <FiyatKontrolUyari sonuc={durum.kontrol} engelMetni="Bu planla sözleşme belgeleri yazdırılamaz." onAraliklar={() => setAraliklarAcik(true)} />
                </div>
            )}

            {/* AI destekli planlama */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg, #faf5ff, #f5f3ff)', border: '1.5px solid #e9d5ff', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
                <i className="fas fa-magic" style={{ color: '#7c3aed', fontSize: 18 }}></i>
                <div style={{ flex: 1, fontSize: 12.5, color: '#4c1d95' }}>
                    <b>AI ile plan oluştur</b> — planı yazıyla tarif edin (ör. "%30 peşin, 6. ayda 500 bin ara ödeme, kalanı 18 ay senetle");
                    eksik bilgi varsa sorulur, tutarlar sistemce hesaplanır.
                </div>
                <button type="button" onClick={() => { if (toplam <= 0) { toast.error('Önce sözleşme fiyatını girin.'); return; } setAiAcik(true); }}
                    style={{ ...btn, background: '#7c3aed', borderColor: '#7c3aed', color: '#fff' }}>
                    <i className="fas fa-magic" style={{ marginRight: 6 }}></i>Sihirbazı aç
                </button>
            </div>
            {aiAcik && (
                <AiOdemePlaniSihirbazi
                    toplam={toplam} sozlesmeTarihi={sozlesmeTarihi} listeFiyati={listeFiyati} kural={kural} kullaniciID={kullaniciID}
                    mevcutKalemSayisi={kalemler.length}
                    onUygula={k => { onChange(k); setAiAcik(false); }}
                    onKapat={() => setAiAcik(false)}
                />
            )}

            {/* Oluşturma araçları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginBottom: 14 }}>
                <div style={kutu}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: '#0f172a' }}><i className="fas fa-layer-group" style={{ color: '#cd6906', marginRight: 6 }}></i>Şablondan oluştur</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <select style={{ ...giris, flex: 2 }} value={sablonId} onChange={e => setSablonId(e.target.value)}>
                            <option value="">{sablonlar.length ? 'Şablon seçin…' : 'Tanımlı şablon yok'}</option>
                            {sablonlar.map(s => <option key={s.sablonId} value={s.sablonId}>{s.sablonAdi}</option>)}
                        </select>
                        <select style={{ ...giris, flex: 1 }} value={sablonTuru} onChange={e => setSablonTuru(e.target.value)}>
                            {ODEME_TURLERI.map(([v, a]) => <option key={v} value={v}>{a}</option>)}
                        </select>
                        <button type="button" style={btn} onClick={sablonUygula} disabled={!sablonId}>Uygula</button>
                    </div>
                </div>
                <div style={kutu}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: '#0f172a' }}><i className="fas fa-calendar-alt" style={{ color: '#cd6906', marginRight: 6 }}></i>Toplu taksit ekle</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.7fr 1.2fr 0.7fr 1fr auto', gap: 6, alignItems: 'end' }}>
                        <div><div style={etiket}>Tutar</div><input style={giris} placeholder={durum.fark > 0 ? Math.round(durum.fark).toLocaleString('tr-TR') : '0'} value={toplu.tutar} onChange={e => setToplu(t => ({ ...t, tutar: e.target.value }))} /></div>
                        <div><div style={etiket}>Adet</div><input style={giris} type="number" min={1} value={toplu.sayi} onChange={e => setToplu(t => ({ ...t, sayi: e.target.value }))} /></div>
                        <div><div style={etiket}>İlk vade</div><TrDateInput style={giris} value={toplu.ilk || ayEkle(sozlesmeTarihi, 1)} onChange={e => setToplu(t => ({ ...t, ilk: e.target.value }))} /></div>
                        <div><div style={etiket}>Her (ay)</div><input style={giris} type="number" min={1} value={toplu.aralik} onChange={e => setToplu(t => ({ ...t, aralik: e.target.value }))} /></div>
                        <div><div style={etiket}>Tür</div>
                            <select style={giris} value={toplu.tur} onChange={e => setToplu(t => ({ ...t, tur: e.target.value }))}>{ODEME_TURLERI.map(([v, a]) => <option key={v} value={v}>{a}</option>)}</select></div>
                        <button type="button" style={btn} onClick={topluEkle}>Ekle</button>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Tutar boşsa planlanmamış tutar taksitlendirilir.</div>
                </div>
            </div>

            {/* Kalem tablosu */}
            <div style={{ border: '1.5px solid #f1f5f9', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', width: 34 }}>#</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', width: 150 }}>Vade</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Açıklama</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', width: 140 }}>Ödeme türü</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', width: 150 }}>Tutar</th>
                            <th style={{ padding: '8px 10px', width: 74 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {kalemler.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Henüz ödeme kalemi yok — şablon uygulayın, toplu taksit ya da aşağıdan tek kalem ekleyin.</td></tr>
                        )}
                        {kalemler.map((k, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #f1f5f9', background: k.kilitli ? '#fffbf5' : undefined }}>
                                <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{i + 1}</td>
                                <td style={{ padding: '6px 10px' }}><TrDateInput style={giris} value={k.vadeTarihi} onChange={e => guncelle(i, { vadeTarihi: e.target.value })} /></td>
                                <td style={{ padding: '6px 10px' }}><input style={giris} value={k.aciklama} onChange={e => guncelle(i, { aciklama: e.target.value })} /></td>
                                <td style={{ padding: '6px 10px' }}>
                                    <select style={giris} value={k.odemeTuru} onChange={e => guncelle(i, { odemeTuru: e.target.value })}>{ODEME_TURLERI.map(([v, a]) => <option key={v} value={v}>{a}</option>)}</select>
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                    <input style={{ ...giris, textAlign: 'right' }} value={k.miktar ? k.miktar.toLocaleString('tr-TR') : ''}
                                        onChange={e => guncelle(i, { miktar: parseFloat(e.target.value.replace(/\./g, '').replace(',', '.')) || 0 })} />
                                </td>
                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                    <button type="button" title={k.kilitli ? 'Kilitli — eşit dağıtımda değişmez (aç)' : 'Kilitle'} onClick={() => onChange(kalemler.map((x, j) => (j === i ? { ...x, kilitli: !x.kilitli } : x)))}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: k.kilitli ? '#cd6906' : '#cbd5e1', padding: 4 }}>
                                        <i className={`fas ${k.kilitli ? 'fa-lock' : 'fa-lock-open'}`}></i>
                                    </button>
                                    <button type="button" title="Sil" onClick={() => onChange(kalemler.filter((_, j) => j !== i))}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}><i className="fas fa-trash"></i></button>
                                </td>
                            </tr>
                        ))}
                        {/* Tek kalem ekle */}
                        <tr style={{ borderTop: '1.5px solid #f1f5f9', background: '#fafbfc' }}>
                            <td style={{ padding: '6px 10px', color: '#cbd5e1' }}><i className="fas fa-plus"></i></td>
                            <td style={{ padding: '6px 10px' }}><TrDateInput style={giris} value={yeni.vade} onChange={e => setYeni(y => ({ ...y, vade: e.target.value }))} /></td>
                            <td style={{ padding: '6px 10px' }}><input style={giris} placeholder="Ör. Peşinat, Ara ödeme" value={yeni.aciklama} onChange={e => setYeni(y => ({ ...y, aciklama: e.target.value }))} /></td>
                            <td style={{ padding: '6px 10px' }}>
                                <select style={giris} value={yeni.tur} onChange={e => setYeni(y => ({ ...y, tur: e.target.value }))}>{ODEME_TURLERI.map(([v, a]) => <option key={v} value={v}>{a}</option>)}</select>
                            </td>
                            <td style={{ padding: '6px 10px' }}><input style={{ ...giris, textAlign: 'right' }} placeholder="Tutar" value={yeni.tutar} onChange={e => setYeni(y => ({ ...y, tutar: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') tekEkle(); }} /></td>
                            <td style={{ padding: '6px 10px' }}><button type="button" style={btn} onClick={tekEkle}>Ekle</button></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button type="button" style={btn} disabled={!kalemler.some(k => !k.kilitli)} onClick={() => onChange(esitDagit(kalemler, toplam))}
                    title="Sözleşme fiyatından kilitli kalemler düşülür, kalan kilitsiz kalemlere eşit bölünür">
                    <i className="fas fa-equals" style={{ marginRight: 5 }}></i>Kalanı eşit dağıt
                </button>
                <button type="button" style={btn} disabled={kalemler.length === 0} onClick={() => onChange(kalemler.map(k => ({ ...k, kilitli: false })))}>
                    <i className="fas fa-lock-open" style={{ marginRight: 5 }}></i>Tüm kilitleri aç
                </button>
                <button type="button" style={{ ...btn, color: '#dc2626' }} disabled={kalemler.length === 0} onClick={() => { if (window.confirm('Tüm ödeme kalemleri silinsin mi?')) onChange([]); }}>
                    <i className="fas fa-trash" style={{ marginRight: 5 }}></i>Temizle
                </button>
            </div>
            {araliklarAcik && kural && (
                <FiyatAraliklariModal kural={kural} listeFiyati={listeFiyati ?? undefined} ortalamaVade={durum.ortalamaVade} onClose={() => setAraliklarAcik(false)} />
            )}
        </div>
    );
};

export default OdemePlaniOlusturucu;
